/**
 * Filter the project grid by a single-select custom field value (Fields v1).
 * Renders nothing when the workspace has no select fields, so it stays out of
 * the way until fields exist. Sort / multi-field Collections are a later pass.
 */
import { Check, Filter, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { useFieldDefs } from "@/hooks/use-fields";
import { cn } from "@/lib/utils";

export type FieldFilter = { fieldDefId: string; value: string } | null;

export function FieldFilterMenu({
  value,
  onChange,
}: {
  value: FieldFilter;
  onChange: (f: FieldFilter) => void;
}) {
  const { data } = useFieldDefs();
  const selectDefs = (data?.fields ?? []).filter((f) => f.type === "select" && f.options.length > 0);
  if (selectDefs.length === 0) return null;

  const active = !!value;
  const activeDef = value ? selectDefs.find((d) => d.id === value.fieldDefId) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Filter by field"
        className={cn(
          "flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors",
          active
            ? "border-primary/60 bg-primary/10 text-foreground"
            : "border-border bg-secondary/40 text-muted-foreground hover:bg-secondary hover:text-foreground",
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">
          {active && activeDef ? `${activeDef.label}: ${value!.value}` : "Filter"}
        </span>
        {active && (
          <span
            role="button"
            aria-label="Clear filter"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="ml-0.5 grid h-4 w-4 place-items-center rounded hover:bg-background/60"
          >
            <X className="h-3 w-3" />
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel>Filter by field</DropdownMenuLabel>
        {selectDefs.map((def) => (
          <DropdownMenuSub key={def.id}>
            <DropdownMenuSubTrigger>{def.label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              {def.options.map((o) => {
                const selected = value?.fieldDefId === def.id && value?.value === o.value;
                return (
                  <DropdownMenuItem
                    key={o.value}
                    onSelect={() => onChange(selected ? null : { fieldDefId: def.id, value: o.value })}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex items-center gap-2">
                      {o.color && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: o.color }} />}
                      {o.value}
                    </span>
                    {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
        {active && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange(null)}>Clear filter</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
